import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSimplifiedUserData } from './SimplifiedUserDataProvider';
import { 
  getCurrentScript, 
  getEffectiveDirection, 
  isCurrentScriptRTL,
  getScriptFont,
  getScriptSettingMessage 
} from '@/lib/utils/scriptUtils';
import { getScriptByCode } from '@/lib/dataSources/scriptTypes';

interface RTLContextType {
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
  currentLanguage: string;
  currentScript: string;
  scriptName: string;
  scriptFont: string;
  scriptMessage: string;
  toggleDirection: () => void;
  setLanguage: (languageCode: string) => void;
  setScript: (scriptCode: string) => void;
}

const RTLContext = createContext<RTLContextType | undefined>(undefined);

// RTL languages list (kept for backward compatibility)
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const RTLProvider = ({ children }: { children: React.ReactNode }) => {
  const { selectedGame, selectedCompany, currentUser } = useSimplifiedUserData();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  
  // Get effective script based on priority: game → company → user → default
  const currentScript = getCurrentScript(selectedGame, selectedCompany, currentUser);
  const direction = getEffectiveDirection(selectedGame, selectedCompany, currentUser);
  const isRTL = isCurrentScriptRTL(selectedGame, selectedCompany, currentUser);
  const scriptFont = getScriptFont(selectedGame, selectedCompany, currentUser);
  const scriptMessage = getScriptSettingMessage(selectedGame, selectedCompany, currentUser);
  
  const scriptMetadata = getScriptByCode(currentScript);
  const scriptName = scriptMetadata?.name || currentScript;

  // Initialize language from localStorage on first load, then fall back to game default
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    } else if (selectedGame?.settings?.defaultLanguage) {
      setCurrentLanguage(selectedGame.settings.defaultLanguage);
    }
  }, [selectedGame]);

  // Update document direction and language based on effective script
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;
    
    // Store language preference in localStorage for persistence
    localStorage.setItem('preferred-language', currentLanguage);
    
    // Apply script-specific font if available
    if (scriptFont) {
      document.documentElement.style.fontFamily = scriptFont;
    }
  }, [direction, currentLanguage, scriptFont]);

  const toggleDirection = () => {
    const newLang = isRTL ? 'en' : 'ar';
    setCurrentLanguage(newLang);
  };

  const setLanguage = (languageCode: string) => {
    setCurrentLanguage(languageCode);
  };

  // Note: setScript doesn't directly change the script here
  // It would need to be implemented at the appropriate level (user/company/game settings)
  // This is here for API consistency
  const setScript = (scriptCode: string) => {
    console.log('setScript called with:', scriptCode);
    console.log('Note: Script changes should be saved via user/company/game settings');
  };

  return (
    <RTLContext.Provider value={{ 
      isRTL, 
      direction, 
      currentLanguage,
      currentScript,
      scriptName,
      scriptFont,
      scriptMessage,
      toggleDirection, 
      setLanguage,
      setScript
    }}>
      {children}
    </RTLContext.Provider>
  );
};

export const useRTL = () => {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useRTL must be used within RTLProvider');
  }
  return context;
};
